import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

const WORDS = ['Painters', 'Decorators', 'Painting Services'];

export default function HeroSection() {
  const [wordIndex, setWordIndex] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [visible, setVisible] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Fade-in on mount
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Typewriter effect
  useEffect(() => {
    const word = WORDS[wordIndex];
    let timeout: ReturnType<typeof setTimeout>;

    if (!deleting && displayed.length < word.length) {
      timeout = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 80);
    } else if (!deleting && displayed.length === word.length) {
      timeout = setTimeout(() => setDeleting(true), 1800);
    } else if (deleting && displayed.length > 0) {
      timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 45);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setWordIndex((i) => (i + 1) % WORDS.length);
    }

    return () => clearTimeout(timeout);
  }, [displayed, deleting, wordIndex]);

  // 3D tilt on mouse move
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      card.style.transform = `perspective(900px) rotateY(${dx * 6}deg) rotateX(${-dy * 4}deg) scale(1.01)`;
    };

    const handleLeave = () => {
      card.style.transform = 'perspective(900px) rotateY(0deg) rotateX(0deg) scale(1)';
    };

    window.addEventListener('mousemove', handleMove);
    card.addEventListener('mouseleave', handleLeave);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      card.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-[60vh] flex items-center justify-center overflow-hidden"
      style={{ background: '#ffffff' }}
    >
      {/* Animated paint blob backgrounds */}
      <div className="absolute top-10 left-10 w-80 h-80 rounded-full blur-3xl animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.25) 0%, transparent 70%)', animationDuration: '4s' }} />
      <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(180,220,200,0.2) 0%, transparent 70%)', animationDuration: '6s' }} />
      <div className="absolute top-1/2 left-5 w-48 h-48 rounded-full blur-2xl animate-pulse"
        style={{ background: 'radial-gradient(circle, rgba(251,146,60,0.15) 0%, transparent 70%)', animationDuration: '5s' }} />

      {/* Floating paint stroke decorations */}
      <div className="absolute top-24 right-24 opacity-20 animate-bounce" style={{ animationDuration: '3s' }}>
        <div className="w-16 h-3 rounded-full bg-amber-400 rotate-12" />
      </div>
      <div className="absolute bottom-32 left-32 opacity-15 animate-bounce" style={{ animationDuration: '4s', animationDelay: '1s' }}>
        <div className="w-24 h-3 rounded-full bg-orange-300 -rotate-6" />
      </div>
      <div className="absolute top-1/3 right-16 opacity-10 animate-bounce" style={{ animationDuration: '5s', animationDelay: '0.5s' }}>
        <div className="w-10 h-10 rounded-full bg-amber-300" />
      </div>
      <div className="absolute bottom-1/3 left-16 opacity-10 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '1.5s' }}>
        <div className="w-6 h-6 rounded-full bg-orange-400" />
      </div>

      {/* Scattered letter particles */}
      {['B','R','U','S','H'].map((letter, i) => (
        <span
          key={letter}
          className="absolute text-amber-200 font-black select-none pointer-events-none"
          style={{
            fontSize: `${3 + i * 0.5}rem`,
            opacity: 0.07 + i * 0.015,
            top: `${10 + i * 15}%`,
            left: `${5 + i * 4}%`,
            animation: `float${i % 2 === 0 ? 'Up' : 'Down'} ${4 + i}s ease-in-out infinite`,
            animationDelay: `${i * 0.4}s`,
          }}
        >
          {letter}
        </span>
      ))}

      {/* Main content card with 3D tilt */}
      <div
        ref={cardRef}
        className="relative z-10 text-center px-6 py-6 sm:px-10 sm:py-10 max-w-4xl mx-auto"
        style={{ transition: 'transform 0.15s ease-out' }}
      >
        {/* Welcome badge */}
        <div
          className="inline-flex items-center gap-2 mb-5 sm:mb-8"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <span className="text-amber-700 text-xs font-semibold tracking-widest uppercase">Welcome to</span>
        </div>

        {/* Main title with letter-by-letter stagger */}
        <div
          className="mb-3 sm:mb-4"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          {'BRUSHOUR'.split('').map((char, i) => (
            <span
              key={i}
              className="inline-block font-black text-neutral-800 tracking-tight"
              style={{
                fontSize: 'clamp(2.8rem, 10vw, 7rem)',
                lineHeight: 1,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.8)',
                transition: `opacity 0.6s ease ${0.3 + i * 0.06}s, transform 0.6s cubic-bezier(0.34,1.56,0.64,1) ${0.3 + i * 0.06}s`,
                textShadow: '3px 3px 0px rgba(251,191,36,0.2)',
              }}
            >
              {char}
            </span>
          ))}
        </div>

        {/* Animated brush stroke underline */}
        <div className="flex justify-center mb-5 sm:mb-8"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.5s ease 1.2s',
          }}
        >
          <div className="relative h-2 sm:h-3 overflow-hidden" style={{ width: '200px' }}>
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: 'linear-gradient(90deg, transparent, #f59e0b, #d97706, #f59e0b, transparent)',
                transform: visible ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 1s cubic-bezier(0.4,0,0.2,1) 1.3s',
              }}
            />
            <div
              className="absolute inset-0 rounded-full opacity-40"
              style={{
                background: 'linear-gradient(90deg, transparent, #fbbf24, transparent)',
                transform: visible ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'left',
                transition: 'transform 1.2s cubic-bezier(0.4,0,0.2,1) 1.4s',
                marginTop: '4px',
                height: '4px',
              }}
            />
          </div>
        </div>

        {/* Typewriter subtitle */}
        <p
          className="text-base sm:text-xl md:text-2xl text-neutral-600 mb-7 sm:mb-10 font-light"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease 0.9s, transform 0.7s ease 0.9s',
          }}
        >
          Your Local{' '}
          <span className="font-semibold text-amber-600 border-r-2 border-amber-500 pr-1" style={{ minWidth: '120px', display: 'inline-block', textAlign: 'left' }}>
            {displayed}
          </span>
        </p>

        {/* CTA buttons */}
        <div
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.7s ease 1.1s, transform 0.7s ease 1.1s',
          }}
        >
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-2xl whitespace-nowrap relative overflow-hidden w-auto justify-center text-sm"
            style={{ background: 'linear-gradient(135deg, #1c1c1c 0%, #3a3a3a 100%)' }}
          >
            <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }} />
            <i className="far fa-check-square relative z-10 text-base" />
            <span className="relative z-10">Get a Free Quote</span>
            <i className="ri-arrow-right-line relative z-10 group-hover:translate-x-1 transition-transform duration-200" />
          </Link>

          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-5 py-2.5 border-2 border-neutral-300 hover:border-amber-400 text-neutral-700 hover:text-amber-700 font-medium rounded-lg transition-all duration-300 whitespace-nowrap bg-white/60 hover:bg-amber-50/60 backdrop-blur-sm w-auto justify-center text-sm"
          >
            <i className="ri-image-line text-base" />
            View Our Work
          </Link>
        </div>

        {/* Trust indicators */}
        <div
          className="mt-8 sm:mt-12 flex flex-wrap justify-center gap-3 sm:gap-6 text-sm text-neutral-500"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.7s ease 1.4s',
          }}
        >
          {[
            { icon: 'ri-star-fill', text: '5★ Rated', color: '#f59e0b' },
            { icon: 'ri-shield-check-line', text: 'Fully Insured', color: '#10b981' },
            { icon: 'ri-map-pin-line', text: 'Local Experts', color: '#6366f1' },
            { icon: 'ri-time-line', text: 'Fast Response', color: '#f97316' },
          ].map(({ icon, text, color }) => (
            <div key={text} className="flex items-center gap-1.5">
              <i className={`${icon} text-base`} style={{ color }} />
              <span className="font-medium text-neutral-600">{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Keyframe styles */}
      <style>{`
        @keyframes floatUp {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        @keyframes floatDown {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(20px) rotate(-5deg); }
        }
      `}</style>
    </section>
  );
}
